// Marco de las pestañas Por comisión, Por tienda y Por producto: filtros, pills, totales y tabla.
import type { ReactNode } from 'react';
import { Button } from '@/kit';
import { UnavailableState } from '@/shared/patterns/DataTable';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { StatsBar, type StatsBarItem } from '@/shared/patterns/StatsBar';
import { TableToolbar } from '@/shared/patterns/TableToolbar';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import type { PayoutPeriod } from '../../lib/payoutPeriods';

/** Lo que reciben las tres pestañas desde la página. */
export interface PaidTabProps {
    period: PayoutPeriod;
    onPeriodChange: (period: PayoutPeriod) => void;
    today: string;
    directory: CommissionDirectory;
}

export function PaidFilters({
    onClear,
    note,
    children,
}: {
    onClear: () => void;
    note?: string;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-3 p-3">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold">Filtros</span>
                <button type="button" className="text-[10.5px] text-primary" onClick={onClear}>
                    Limpiar
                </button>
            </div>
            {children}
            {note && <p className="text-[10px] text-muted-foreground">{note}</p>}
        </div>
    );
}

/** La ficha cuando la fuente falló: sin montos que parezcan dato. */
export function PaidDetailUnavailable() {
    return (
        <MasterDetailEmpty
            title="Sin datos del periodo"
            sub="No se pudo cargar lo liquidado; reintenta desde la lista."
        />
    );
}

interface PaidListFrameProps<K extends string> {
    pills: Array<StatsBarItem<K>>;
    pill: K;
    onPill: (key: K) => void;
    pillsLabel: string;
    metrics: ReactNode;
    error: Error | null;
    errorTitle: string;
    onRetry: () => void;
    children: ReactNode;
}

export function PaidListFrame<K extends string>(props: PaidListFrameProps<K>) {
    const { pills, pill, onPill, pillsLabel, metrics, error, errorTitle, onRetry, children } =
        props;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StatsBar items={pills} active={pill} onChange={onPill} ariaLabel={pillsLabel} />
            <TableToolbar>
                <div className="flex-1" />
                {metrics}
            </TableToolbar>
            <div className="min-h-0 flex-1 overflow-auto">
                {error ? (
                    <UnavailableState
                        title={errorTitle}
                        body={error.message}
                        action={
                            <Button variant="outline" size="sm" onClick={onRetry}>
                                Reintentar
                            </Button>
                        }
                    />
                ) : (
                    children
                )}
            </div>
        </div>
    );
}
